import { Img } from '@/components/Img.tsx';
import { Layout } from '@/components/Layout.tsx';
import { Icons } from '@/components/ui.tsx';
import {
  CREDENTIALS,
  PROBLEM_CARDS,
  SOLUTION_PILLARS,
} from '@/data/content.ts';
import { asset, href } from '@/lib/paths.ts';

export function Home() {
  return (
    <Layout currentPath="/">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero__image-wrap">
          <Img
            src="images/hero-student.webp"
            alt="Student writing in classroom"
            width={1376}
            height={768}
            priority
            className="hero__image"
          />
        </div>
        <div className="hero__overlay" aria-hidden="true" />
        <div className="container hero__content">
          <h1>
            What if every student
            <br />
            had a personal tutor?
          </h1>
          <p className="hero__lede">
            In 1984, researcher Benjamin Bloom discovered that one-on-one
            tutoring improved student performance by 2 standard deviations
            &mdash; outperforming 98% of traditionally taught students. The
            problem? It&apos;s impossible to scale. Until now.
          </p>
          <div className="hero__actions">
            <a href="#solution" className="btn btn--primary">
              What We Solved
            </a>
            <a href={href('/contact')} className="btn btn--glass">
              Talk to Us
            </a>
          </div>
        </div>
      </section>

      {/* Section 2: Problem in Classrooms */}
      <section className="section-problem" id="problem">
        <div className="container">
          <h2 className="fade-in">
            Current Challenges Education faces with AI
          </h2>
          <div className="problem-grid">
            {PROBLEM_CARDS.map((card) => (
              <div className="problem-card fade-in" key={card.alt}>
                <div className="problem-card__image-wrap">
                  <img
                    src={asset(card.image)}
                    alt={card.alt}
                    width={card.imageWidth}
                    height={card.imageHeight}
                    loading="lazy"
                    className="problem-card__image"
                  />
                </div>
                <div className="problem-card__body">
                  <p>{card.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 3: Solution (2 Sigma Problem) */}
      <section className="section-solution" id="solution">
        <div className="container">
          <div className="solution-intro fade-in">
            <h2>We solved the 2 Sigma Problem</h2>
            <p>
              Bloom proved personalized tutoring is the gold standard. LearnAI
              brings that to every student - where the teacher stays in control.
            </p>
          </div>
          <div className="solution-grid">
            {SOLUTION_PILLARS.map((pillar) => (
              <div className="solution-card fade-in" key={pillar.title}>
                <div className="solution-card__icon-box">
                  {Icons[pillar.icon]('solution-card__icon')}
                </div>
                <h3>
                  {pillar.title === 'Real Understanding, Not Just Grades' ? (
                    <>
                      Real Understanding, Not Just
                      <br />
                      Grades
                    </>
                  ) : (
                    pillar.title
                  )}
                </h3>
                <p>{pillar.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 4: Our Tool */}
      <section className="section-tool" id="tool">
        <div className="container">
          <h2 className="fade-in">Our tool</h2>
          <div className="tool-grid">
            <div className="tool-card fade-in">
              <div className="tool-card__header">
                <span className="tool-card__badge tool-card__badge--neutral">
                  Traditional Chatbot
                </span>
                <h3>Not a Chatbot</h3>
              </div>
              <p className="tool-card__desc">
                Generic AI chatbots give answers directly, encouraging passive
                copy-pasting and bypassing the student&apos;s critical thinking.
              </p>
              <ul className="tool-card__list">
                <li className="tool-card__item">
                  <svg
                    className="tool-card__item-icon tool-card__item-icon--cross"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>Provides direct answers rather than teaching</span>
                </li>
                <li className="tool-card__item">
                  <svg
                    className="tool-card__item-icon tool-card__item-icon--cross"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>
                    Encourages copy-paste completion over comprehension
                  </span>
                </li>
                <li className="tool-card__item">
                  <svg
                    className="tool-card__item-icon tool-card__item-icon--cross"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>
                    Leaves teachers in the dark with no curriculum alignment
                  </span>
                </li>
              </ul>
            </div>

            <div className="tool-card tool-card--accent fade-in">
              <div className="tool-card__header">
                <span className="tool-card__badge tool-card__badge--accent">
                  LearnAI Platform
                </span>
                <h3>Proactive intelligent tutoring system</h3>
              </div>
              <p className="tool-card__desc">
                LearnAI proactively guides students through Socratic inquiry,
                verifying true understanding while keeping teachers in complete
                control.
              </p>
              <ul className="tool-card__list">
                <li className="tool-card__item">
                  <svg
                    className="tool-card__item-icon tool-card__item-icon--check"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>
                    Proactive Socratic guidance that builds critical thinking
                  </span>
                </li>
                <li className="tool-card__item">
                  <svg
                    className="tool-card__item-icon tool-card__item-icon--check"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>
                    Verifies concept mastery before allowing students to advance
                  </span>
                </li>
                <li className="tool-card__item">
                  <svg
                    className="tool-card__item-icon tool-card__item-icon--check"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>
                    Teachers define curriculum context, guardrails, and
                    objectives
                  </span>
                </li>
              </ul>
            </div>
          </div>
          <div className="tool-actions fade-in">
            <a href={href('/about')} className="btn btn--primary">
              What&apos;s the difference?
            </a>
          </div>
        </div>
      </section>

      {/* Section 5: Hear From Our Users */}
      <section className="section-testimonials" id="testimonials">
        <div className="container">
          <h2 className="fade-in">Hear from our users</h2>
          <div className="testimonials-grid">
            <div className="testimonial-card fade-in">
              <span className="testimonial-card__quote-mark" aria-hidden="true">
                &ldquo;
              </span>
              <p className="testimonial-card__quote">
                &ldquo;I want to discuss <strong>with</strong> rather than teach{' '}
                <strong>at</strong> my students, this tool helps me do
                that&rdquo;
              </p>
              <div className="testimonial-card__footer">
                <div className="testimonial-card__avatar" aria-hidden="true">
                  UP
                </div>
                <div className="testimonial-card__author">UVU professor</div>
              </div>
            </div>

            <div className="testimonial-card fade-in">
              <span className="testimonial-card__quote-mark" aria-hidden="true">
                &ldquo;
              </span>
              <p className="testimonial-card__quote">
                &ldquo;I wish this could be my homework all the time, I feel I
                could participate more in class&rdquo;
              </p>
              <div className="testimonial-card__footer">
                <div className="testimonial-card__avatar" aria-hidden="true">
                  US
                </div>
                <div className="testimonial-card__author">UVU Student</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 6: Credentials Strip */}
      <section className="credentials-strip" id="about">
        <div className="container credentials-inner">
          <div className="credentials-row">
            <span className="credential-item">{CREDENTIALS[0]}</span>
            <span className="credential-item">{CREDENTIALS[1]}</span>
          </div>
          <div className="credentials-row">
            <span className="credential-item">{CREDENTIALS[2]}</span>
            <span className="credential-item">{CREDENTIALS[3]}</span>
          </div>
        </div>
      </section>

      {/* Section 5: CTA */}
      <section className="section-cta" id="pricing">
        <div className="container cta-content fade-in">
          <h2>
            Ready to bring real learning back
            <br />
            to your classroom?
          </h2>
          <a href={href('/contact')} className="btn btn--primary btn--cta">
            Get in Touch
          </a>
        </div>
      </section>
    </Layout>
  );
}
